import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import { useCause } from "contexts/CauseContext";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { appConfig } from "../../config/appConfig";
import { handleTestAccountBypass } from "../../utils/auth/handleTestAccountBypass";
import { sendMagicLinkEmail } from "../../utils/auth/sendMagicLinkEmail";
import { useUserLogin } from "../authentication/hooks/useUserAuth";
import { getAccount } from "../../api/accountApi";

export type AfterFirebaseAuthBeforeLoginUserArgs = {
  userUid: string;
  email: string;
  isExistingUser: boolean;
};

export type UseAuthContainerOptions = {
  afterFirebaseAuthBeforeLoginUser?: (
    args: AfterFirebaseAuthBeforeLoginUserArgs
  ) => Promise<void>;
};

export type UseAuthContainerReturn = {
  error: string;
  emailForMagic: string;
  signUp: boolean;
  isLoading: boolean;
  successMessage: string;
  showCodeInput: boolean;
  introCompleted: boolean;

  setEmailForMagic: (email: string) => void;
  handleIntroComplete: () => void;
  sendMagicLink: () => Promise<void>;
  handleCodeVerified: () => Promise<void>;
  handleBackToEmail: () => void;

  isOnline: boolean;
  loadingStates: any;
};

export function useLoginPage(
  options: UseAuthContainerOptions = {}
): UseAuthContainerReturn {
  const { afterFirebaseAuthBeforeLoginUser } = options;
  const [error, setError] = useState("");
  const [emailForMagic, setEmailForMagic] = useState("");
  const [signUp, setSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [introCompleted, setIntroCompleted] = useState(true); // TEMPORARILY DISABLED: Set to true to skip animated intro

  const { loadingStates, refreshData, setMoods } = useAppState();
  const { setUserId, setEmail } = useAuth();
  const { setCauses } = useCause();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const networkStatus = useNetworkStatus() as {
    isOnline: boolean;
    isOffline: boolean;
    isSlowConnection: boolean;
    connectionType: string;
  };
  const isOnline = networkStatus.isOnline;
  const loginUser = useUserLogin({
    setUserId,
    setEmail,
    setMoods,
    setCauses,
    refreshData,
  });

  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === "magic-link-expired") {
      setError(
        "Magic link has expired or already been used. Please request a new one."
      );
    } else if (errorParam === "invalid-email") {
      setError("Invalid email address. Please check and try again.");
    }
  }, [searchParams]);

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam?.trim()) {
      setEmailForMagic(emailParam.trim());
    }
  }, [searchParams]);

  const handleIntroComplete = () => {
    setIntroCompleted(true);
    setSignUp(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("tunded_intro_seen", "true");
    }
  };

  const sendMagicLink = async () => {
    try {
      setSuccessMessage("");
      setError("");

      const email = emailForMagic.trim();
      if (!email) {
        setError("Enter email to receive a sign-in link.");
        return;
      }

      if (loadingStates?.users) {
        setError("Loading user data, please wait...");
        return;
      }

      // DEVELOPMENT BYPASS: Skip magic link for experienceapp.io test emails or dev hostnames
      const isProductionTestEmail =
        window.location.hostname === appConfig.productionHostname &&
        appConfig.testEmails.includes(email);

      const isDevelopmentTestEmail =
        window.location.hostname === appConfig.developmentHostname &&
        appConfig.testEmails.includes(email);

      const isBypassAuthHostname = appConfig.bypassAuthHostnames.includes(
        window.location.hostname
      );

      console.log("isBypassAuthHostname:", isBypassAuthHostname);
      console.log("email: " + email);

      if (
        isProductionTestEmail ||
        isBypassAuthHostname ||
        isDevelopmentTestEmail
      ) {
        setIsLoading(true);

        const result = await handleTestAccountBypass({
          email,
          setUserId,
          setEmail,
          setMoods,
          setCauses,
          refreshData,
          navigate,
        });

        if (!result.success) {
          setError(result.error || "Test account setup error");
        }
        setIsLoading(false);
        return;
      }

      console.log("email2");
      // Production magic link flow
      setIsLoading(true);
      setError("");
      setSuccessMessage("");
      console.log("email3");

      await sendMagicLinkEmail(email);
      setSuccessMessage("");
      setShowCodeInput(true);
      console.log("email4");
    } catch (e: any) {
      console.error("sendMagicLink error:", e);
      setError(`Could not send the link: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeVerified = async () => {
    try {
      setIsLoading(true);
      setError("");

      const email = emailForMagic.trim().toLowerCase();

      const { auth } = await import("../../config/firebase");
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error(
          "User not authenticated. Please verify the code again."
        );
      }

      const userUid = firebaseUser.uid;

      const existingAccount = await getAccount().catch(() => null);
      const isExistingUser = !!existingAccount;

      if (afterFirebaseAuthBeforeLoginUser) {
        await afterFirebaseAuthBeforeLoginUser({
          userUid,
          email,
          isExistingUser,
        });
      }

      await loginUser(email, userUid, isExistingUser);
    } catch (e: any) {
      console.error("Code verification error:", e);
      setError(`Login failed: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setSuccessMessage("");
    setError("");
  };

  return {
    error,
    emailForMagic,
    signUp,
    isLoading,
    successMessage,
    showCodeInput,
    introCompleted,

    setEmailForMagic,
    handleIntroComplete,
    sendMagicLink,
    handleCodeVerified,
    handleBackToEmail,

    isOnline,
    loadingStates,
  };
}
